import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../components/Button";
import { LinkButton } from "../../../../components/LinkButton";
import { TextArea } from "../../../../components/TextArea";
import { resolveTrigger } from "../../../../hooks/queries/patients";
import {
  Component,
  ComponentTitle,
  Container,
  ResolveTriggerContainer,
  Row,
  SubmitContainer,
  Title,
} from "./ResolveTriggerModal.styles";

type ResolveTriggerModalProps = {
  patientId: number;
  alertIds: string[];
  type: string;
  triggered: string;
  /**
   * Set when several alerts are being resolved at once, in which case there is no single note to
   * attach. This used to be inferred by comparing `type` against the literal "Multiple selected",
   * which is now a translated string — so the note field would have reappeared in every language
   * but English (EN-5191).
   */
  isMultiple?: boolean;
  onClose: (showNotification: boolean, success?: boolean) => void;
};

export function ResolveTriggerModal({
  patientId,
  alertIds,
  type,
  triggered,
  isMultiple = false,
  onClose,
}: ResolveTriggerModalProps) {
  const { t } = useTranslation();

  const [note, setNote] = useState<string>();

  const handleClose = () => {
    onClose(false);
  };

  const submitResolveTrigger = async () => {
    if (alertIds.length > 0) {
      const resp = await resolveTrigger(alertIds, patientId, null, note);
      onClose(true, resp.success);
    } else {
      handleClose();
    }
  };

  return (
    <Container>
      <Title>{t("patientPage_resolveTrigger_title")}</Title>
      <ResolveTriggerContainer>
        <Row>
          <ComponentTitle>
            {t("patientPage_resolveTrigger_type")}
          </ComponentTitle>
          <Component>{type}</Component>
        </Row>
        <Row>
          <ComponentTitle>
            {t("patientPage_resolveTrigger_triggered")}
          </ComponentTitle>
          <Component>{triggered}</Component>
        </Row>
        {!isMultiple ? (
          <Row>
            <ComponentTitle>
              {t("patientPage_resolveTrigger_note")}
            </ComponentTitle>
            <Component>
              <TextArea
                placeholder={t("patientPage_resolveTrigger_notePlaceholder")}
                onChange={(event) => setNote(event.target.value)}
                backgroudColor="white"
                rows={4}
              />
            </Component>
          </Row>
        ) : null}
      </ResolveTriggerContainer>
      <SubmitContainer>
        <Button
          label={t("patientPage_resolveTrigger_resolve")}
          type="button"
          onClick={submitResolveTrigger}
        />
        <LinkButton
          label={t("common_cancel")}
          size="lg"
          color="black"
          fontWeight="semiBold"
          onClick={handleClose}
        />
      </SubmitContainer>
    </Container>
  );
}
